import { AuthStorage } from "utils/auth-storage";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { SideBarEndService } from "apis/Services/SideBarService";
import { useParams } from "react-router-dom";
import styles from "./bulk-edit-modal.module.css";

const BULK_EDIT_FIELDS = [
  { field: "specNumber", type: "string" },
  { field: "specRevision", type: "int" },
  { field: "specType", type: "string" },
  { field: "responsibleContractor", type: "string" },
  { field: "contractIssuedRelease", type: "date" },
  { field: "submittalPrepTime", type: "int" },
  { field: "approvalTime", type: "int" },
  { field: "leadTime", type: "int" },
  { field: "dateRequiredOnSite", type: "date" },
  { field: "scheduleID", type: "schedule" },
  { field: "subCommittedDeliveryDate", type: "date" },
  { field: "actualDeliveryDate", type: "date" },
];

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Creates the empty field map used whenever the inline bulk edit panel opens.
function buildEmptyBulkEditValues() {
  return BULK_EDIT_FIELDS.reduce((accumulator, fieldConfig) => {
    accumulator[fieldConfig.field] = undefined;
    return accumulator;
  }, {});
}

// Parses project date strings into Day.js objects for the bulk edit controls.
function parseCalendarDate(value) {
  if (!value) return null;

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [month, day, year] = value.split("-");
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate : null;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
    const [month, day, year] = value.split("/");
    const parsedDate = dayjs(`${year}-${month}-${day}`);
    return parsedDate.isValid() ? parsedDate : null;
  }

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate : null;
}

// Reads working-day flags from the project calendar settings.
function isWorkingDayEnabled(value) {
  return value === "1" || value === 1 || value === true;
}

// Builds disabled weekdays and holiday dates for the inline bulk edit date pickers.
function buildDisabledCalendarConfig(workingDays, nonWorkingDays) {
  const disabledWeekdays = new Set(
    DAY_NAMES.filter(
      (dayName) =>
        Object.prototype.hasOwnProperty.call(workingDays || {}, dayName) &&
        !isWorkingDayEnabled(workingDays?.[dayName]),
    ),
  );

  const disabledDates = new Set();

  (nonWorkingDays || []).forEach((holiday) => {
    const startDate = parseCalendarDate(holiday?.startDate);
    const finishDate = parseCalendarDate(holiday?.finishDate);

    if (!startDate || !finishDate) return;

    const rangeStart = startDate.isAfter(finishDate) ? finishDate : startDate;
    const rangeEnd = startDate.isAfter(finishDate) ? startDate : finishDate;

    let cursor = rangeStart;
    while (!cursor.isAfter(rangeEnd, "day")) {
      disabledDates.add(cursor.format("YYYY-MM-DD"));
      cursor = cursor.add(1, "day");
    }
  });

  return { disabledWeekdays, disabledDates };
}

// Keeps legacy date behavior that blocks future dates for specific fields.
function disallowFutureDates(field) {
  return field === "contractIssuedRelease" || field === "actualDeliveryDate";
}

// Resolves a human-readable label for each editable procurement field.
function resolveFieldLabel(field, alignedData) {
  return alignedData?.[field]?.label || field;
}

// Converts the filled inline form fields into a multi-row patch object.
function buildBulkEditPatch(formValues, allowedFields = null) {
  const rowPatch = {};

  BULK_EDIT_FIELDS.forEach(({ field, type }) => {
    if (allowedFields && !allowedFields.has(field)) return;

    const rawValue = formValues[field];

    if (rawValue === undefined || rawValue === null) return;

    if (type === "string" || type === "schedule") {
      if (String(rawValue).trim() === "") return;
      rowPatch[field] = rawValue;
      return;
    }

    if (type === "date") {
      if (!dayjs.isDayjs(rawValue) || !rawValue.isValid()) return;
      rowPatch[field] = rawValue.format("YYYY-MM-DD");
      return;
    }

    rowPatch[field] = rawValue;
  });

  return rowPatch;
}

// Renders the inline bulk edit panel for multi-row updates and assignments.
function ProcurementBulkEditModal({
  open,
  onCancel,
  onApply,
  alignedData,
  accessLevel,
  columnPermissions,
  selectedCount,
  schedule,
  scheduleOptions,
  workingDays,
  nonWorkingDays,
}) {
  const { company_id, id } = useParams();
  const [formValues, setFormValues] = useState(buildEmptyBulkEditValues);
  const [contactInfo, setContactInfo] = useState(null);
  const [isContactsLoading, setIsContactsLoading] = useState(false);
  const [selectedManagerEmails, setSelectedManagerEmails] = useState([]);
  const [selectedContractorEmails, setSelectedContractorEmails] = useState([]);
  const [selectedManagerEmailsToRemove, setSelectedManagerEmailsToRemove] =
    useState([]);
  const [
    selectedContractorEmailsToRemove,
    setSelectedContractorEmailsToRemove,
  ] = useState([]);
  const [sendNotification, setSendNotification] = useState(true);

  useEffect(() => {
    if (!open) return;
    setFormValues(buildEmptyBulkEditValues());
    setSelectedManagerEmails([]);
    setSelectedContractorEmails([]);
    setSelectedManagerEmailsToRemove([]);
    setSelectedContractorEmailsToRemove([]);
    setSendNotification(true);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const code = AuthStorage.getItem("token");
    const loginEmail = AuthStorage.getItem("email");
    setIsContactsLoading(true);

    SideBarEndService.TasksAssignedInfo({
      code,
      loginEmail,
      company_id,
      project_id: id,
      id,
    })
      .then((response) => {
        setContactInfo(response);
      })
      .catch((error) => {
        console.error("Failed to load assignable contacts", error);
      })
      .finally(() => {
        setIsContactsLoading(false);
      });
  }, [open, company_id, id]);

  const calendarConfig = useMemo(
    () => buildDisabledCalendarConfig(workingDays, nonWorkingDays),
    [workingDays, nonWorkingDays],
  );

  const allowedBulkEditFields = useMemo(() => {
    if (accessLevel > 1) {
      return new Set(BULK_EDIT_FIELDS.map(({ field }) => field));
    }

    const unlockedColumns = columnPermissions?.unlockedColumns;
    return new Set(
      BULK_EDIT_FIELDS.map(({ field }) => field).filter((field) =>
        unlockedColumns?.has(field),
      ),
    );
  }, [accessLevel, columnPermissions]);

  const hasPendingChanges = useMemo(
    () =>
      Object.keys(buildBulkEditPatch(formValues, allowedBulkEditFields))
        .length > 0 ||
      (accessLevel > 1 &&
        (selectedManagerEmails.length > 0 ||
          selectedContractorEmails.length > 0)) ||
      (accessLevel > 2 &&
        (selectedManagerEmailsToRemove.length > 0 ||
          selectedContractorEmailsToRemove.length > 0)),
    [
      accessLevel,
      allowedBulkEditFields,
      formValues,
      selectedContractorEmails.length,
      selectedContractorEmailsToRemove.length,
      selectedManagerEmails.length,
      selectedManagerEmailsToRemove.length,
    ],
  );

  const assignableContacts = useMemo(
    () => contactInfo?.validContacts || {},
    [contactInfo],
  );

  const managerOptions = useMemo(
    () =>
      Object.entries(assignableContacts)
        .filter(([email]) => !selectedContractorEmails.includes(email))
        .map(([email, [firstName = "", lastName = ""]]) => ({
          label: `${firstName} ${lastName}`.trim() || email,
          value: email,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [assignableContacts, selectedContractorEmails],
  );

  const contractorOptions = useMemo(
    () =>
      Object.entries(assignableContacts)
        .filter(([email]) => !selectedManagerEmails.includes(email))
        .map(
          ([
            email,
            [firstName = "", lastName = "", _emailAddress, companyName = ""],
          ]) => ({
            label: companyName
              ? `${firstName} ${lastName} - ${companyName}`.trim()
              : `${firstName} ${lastName}`.trim() || email,
            value: email,
          }),
        )
        .sort((left, right) => left.label.localeCompare(right.label)),
    [assignableContacts, selectedManagerEmails],
  );

  const removeManagerOptions = useMemo(
    () =>
      Object.entries(assignableContacts)
        .filter(([email]) => !selectedContractorEmailsToRemove.includes(email))
        .map(([email, [firstName = "", lastName = ""]]) => ({
          label: `${firstName} ${lastName}`.trim() || email,
          value: email,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [assignableContacts, selectedContractorEmailsToRemove],
  );

  const removeContractorOptions = useMemo(
    () =>
      Object.entries(assignableContacts)
        .filter(([email]) => !selectedManagerEmailsToRemove.includes(email))
        .map(
          ([
            email,
            [firstName = "", lastName = "", _emailAddress, companyName = ""],
          ]) => ({
            label: companyName
              ? `${firstName} ${lastName} - ${companyName}`.trim()
              : `${firstName} ${lastName}`.trim() || email,
            value: email,
          }),
        )
        .sort((left, right) => left.label.localeCompare(right.label)),
    [assignableContacts, selectedManagerEmailsToRemove],
  );

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleScheduleChange = (nextValue) => {
    const matchingScheduleActivity = (schedule || []).find(
      (scheduleItem) => Object.keys(scheduleItem || {})[0] === nextValue,
    );
    const scheduledStartDate = nextValue
      ? parseCalendarDate(matchingScheduleActivity?.[nextValue]?.startDate)
      : null;

    setFormValues((prev) => ({
      ...prev,
      scheduleID: nextValue || undefined,
      dateRequiredOnSite: nextValue
        ? scheduledStartDate && scheduledStartDate.isValid()
          ? scheduledStartDate
          : undefined
        : undefined,
    }));
  };

  const handleApply = () => {
    const rowPatch = buildBulkEditPatch(formValues, allowedBulkEditFields);
    if (
      !Object.keys(rowPatch).length &&
      selectedManagerEmails.length === 0 &&
      selectedContractorEmails.length === 0 &&
      selectedManagerEmailsToRemove.length === 0 &&
      selectedContractorEmailsToRemove.length === 0
    ) {
      return;
    }

    onApply?.({
      rowPatch,
      assignmentData: {
        managerEmails: selectedManagerEmails,
        contractorEmails: selectedContractorEmails,
        action: sendNotification ? "sendNotification" : "",
      },
      removalData: {
        managerEmails: selectedManagerEmailsToRemove,
        contractorEmails: selectedContractorEmailsToRemove,
      },
    });
  };

  if (!open) return null;

  const visibleFields = BULK_EDIT_FIELDS.filter(
    ({ field }) =>
      alignedData?.[field]?.hidden !== "true" &&
      alignedData?.[field]?.editable !== "false",
  );

  const isFieldDisabled = (field) =>
    accessLevel < 2 && !allowedBulkEditFields.has(field);

  return (
    <div className={styles.inlinePanel}>
      {/* Header row */}
      <div className={styles.inlinePanelHeader}>
        <div className={styles.inlinePanelHeaderLeft}>
          <span className={styles.inlinePanelTitle}>Bulk Edit</span>
          <span className={styles.inlinePanelSubtitle}>
            Only filled fields will be updated. Blank fields are ignored.
          </span>
        </div>
        <div className={styles.inlinePanelHeaderRight}>
          <Tag className={styles.selectionTag}>
            {selectedCount} submittal{selectedCount === 1 ? "" : "s"} selected
          </Tag>
          <button
            type="button"
            className={styles.inlinePanelClose}
            onClick={onCancel}
            title="Close bulk edit"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Fields grid */}
      <div className={styles.inlinePanelGrid}>
        {visibleFields.map(({ field, type }) => (
          <div key={field} className={styles.inlinePanelField}>
            <label
              title={resolveFieldLabel(field, alignedData)}
              className={`${styles.label} ${
                isFieldDisabled(field) ? styles.labelDisabled : ""
              }`}
            >
              {resolveFieldLabel(field, alignedData)}
            </label>

            {type === "string" && (
              <Input
                className={styles.input}
                value={formValues[field]}
                onChange={(e) => handleFieldChange(field, e.target.value)}
                allowClear
                size="small"
                disabled={isFieldDisabled(field)}
              />
            )}

            {type === "int" && (
              <InputNumber
                className={styles.numberInput}
                value={formValues[field]}
                onChange={(value) => handleFieldChange(field, value)}
                controls={false}
                size="small"
                disabled={isFieldDisabled(field)}
                style={{ width: "100%" }}
              />
            )}

            {type === "date" && (
              <DatePicker
                className={styles.datePicker}
                value={formValues[field]}
                format="MM-DD-YYYY"
                allowClear
                size="small"
                disabled={isFieldDisabled(field)}
                onChange={(value) =>
                  handleFieldChange(field, value || undefined)
                }
                disabledDate={(current) => {
                  if (!current) return false;
                  const isBlockedByCalendar =
                    calendarConfig.disabledWeekdays.has(
                      current.format("dddd"),
                    ) ||
                    calendarConfig.disabledDates.has(
                      current.format("YYYY-MM-DD"),
                    );
                  const isFutureDateBlocked =
                    disallowFutureDates(field) &&
                    current.isAfter(dayjs().endOf("day"));
                  return isBlockedByCalendar || isFutureDateBlocked;
                }}
              />
            )}

            {type === "schedule" && (
              <Select
                showSearch
                allowClear
                className={styles.select}
                value={formValues[field]}
                options={scheduleOptions}
                optionFilterProp="label"
                // placeholder="Schedule ID"
                size="large"
                disabled={isFieldDisabled(field)}
                onChange={(value) => handleScheduleChange(value)}
              />
            )}
          </div>
        ))}
      </div>

      <div className={styles.sectionDivider} />

      <div className={styles.assignmentSection}>
        <div className={styles.assignmentHeader}>
          <span className={styles.assignmentTitle}>Bulk Assign</span>
          {accessLevel < 2 && (
            <span className={styles.assignmentSubtitle}>
              Assignment is unavailable for your access level.
            </span>
          )}
        </div>

        <div className={styles.assignmentGrid}>
          <div className={styles.inlinePanelField}>
            <label className={styles.label}>Assign Manager(s)</label>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              className={`${styles.select} ${styles.assignmentSelect}`}
              value={selectedManagerEmails}
              options={managerOptions}
              onChange={setSelectedManagerEmails}
              size="large"
              placeholder="Select manager(s)"
              disabled={accessLevel < 2 || isContactsLoading}
              loading={isContactsLoading}
            />
          </div>

          <div className={styles.inlinePanelField}>
            <label className={styles.label}>Assign Contractor(s)</label>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              className={`${styles.select} ${styles.assignmentSelect}`}
              value={selectedContractorEmails}
              options={contractorOptions}
              onChange={setSelectedContractorEmails}
              size="large"
              placeholder="Select contractor(s)"
              disabled={accessLevel < 2 || isContactsLoading}
              loading={isContactsLoading}
            />
          </div>
        </div>

        <div className={styles.switchRow}>
          <Switch
            checked={sendNotification}
            onChange={setSendNotification}
            disabled={accessLevel < 2}
          />
          <span>Notify assigned users</span>
        </div>
      </div>

      <div className={styles.sectionDivider} />

      <div className={styles.assignmentSection}>
        <div className={styles.assignmentHeader}>
          <span className={styles.assignmentTitle}>Bulk Unassign</span>
          {accessLevel < 3 && (
            <span className={styles.assignmentSubtitle}>
              Removal is unavailable for your access level.
            </span>
          )}
        </div>

        <div className={styles.assignmentGrid}>
          <div className={styles.inlinePanelField}>
            <label className={styles.label}>Remove Assigned Manager(s)</label>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              className={`${styles.select} ${styles.assignmentSelect}`}
              value={selectedManagerEmailsToRemove}
              options={removeManagerOptions}
              onChange={setSelectedManagerEmailsToRemove}
              size="large"
              placeholder="Select manager(s)"
              disabled={accessLevel < 3 || isContactsLoading}
              loading={isContactsLoading}
            />
          </div>

          <div className={styles.inlinePanelField}>
            <label className={styles.label}>
              Remove Assigned Contractor(s)
            </label>
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              className={`${styles.select} ${styles.assignmentSelect}`}
              value={selectedContractorEmailsToRemove}
              options={removeContractorOptions}
              onChange={setSelectedContractorEmailsToRemove}
              size="large"
              placeholder="Select contractor(s)"
              disabled={accessLevel < 3 || isContactsLoading}
              loading={isContactsLoading}
            />
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div className={styles.inlinePanelFooter}>
        <Button
          size="small"
          className={styles.secondaryButton}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="small"
          className={styles.primaryButton}
          type="primary"
          disabled={!hasPendingChanges}
          onClick={handleApply}
        >
          Apply Changes
        </Button>
      </div>
    </div>
  );
}

export default ProcurementBulkEditModal;
